import React from "react";
import {
  GPSyncStatusCode,
  MaterialCategoryName,
  MaterialUnit,
} from "fe-shared/src/types/domain";

import { DocumentsResponse } from "./details";

import { InvoiceMaterial } from "./materials";
import { NoteRecord } from "./notes";

export interface RowInvoice {
  key?: string;
  name: React.ReactNode;
  value: string | number;
  rate?: string | number;
  additionalValue?: string | number;
  isTitle?: boolean;
  withBorder?: boolean;
  withBoldBorder?: boolean;
  isTotal?: boolean;
  isSubtotal?: boolean;
  isGrandTotal?: boolean;
  isRedValue?: boolean;
  valuesStyles?: string;
}

export enum InvoiceItemType {
  Transaction = "Transaction",
  Transfer = "Transfer",
}

export enum InvoiceTransactionType {
  DeliveryToProcessor = "Delivery",
  DispositionToRPM = "Disposition to RPM",
  DispositionToReuse = "Disposition to Reuse Market",
  DispositionToEnergy = "Disposition to Energy/Fuel",
  DispositionToLandfill = "Disposition to Landfill",
  DispositionToRecycling = "Disposition to Recycling",
  SpecialHandling = "Special Handling",
}

export interface InvoiceMaterialGroup {
  unit: MaterialUnit;
  transactionType: InvoiceTransactionType;
  categoryName: MaterialCategoryName;
  materials: InvoiceMaterial[];
}

export interface InvoiceCreateInfo<T> {
  isHSTExemptChecked: boolean;
  transactions: T[];
  transactionNumbers: string[];
  startDate: string;
  endDate: string;
  invoiceTarget: InvoiceItemType;
  subtotal: number;
  invoiceFees: InvoiceFee[];
  invoiceFeeSubTotal: number;
  transactionFeeSubTotal: number;
  unidentifiedMaterials: InvoiceMaterial[];
  paymentBalanceAdjustments: BalanceAdjustment[];
  paymentBalanceAdjustmentsSubTotal: number;
  hstTax: number;
}

export enum InvoiceStatusCode {
  Submitted = "Submitted",
  InReview = "InReview",
  InApproval = "InApproval",
  Approved = "Approved",
}

export interface GridInvoice {
  invoiceId: number;
  invoiceNumber: string;
  submittedDate: string;
  invoiceTarget: InvoiceItemType;
  isHSTExemptChecked: boolean;
  company?: {
    businessRole: string;
    displayName: string;
    id: number;
    regNumber: string;
    isActive: boolean;
  };
  periodStartDate: string;
  periodEndDate: string;
  sum: number;
  hstTax: number;
  hstRegistrationNumber: string;
  grandTotal: number;
  statusCode: InvoiceStatusCode;
  assignee?: {
    displayName: string;
    email: string | null;
    userId: number;
  };
}

export interface GridInvoiceExport extends GridInvoice {
  weight: number;
  historyLogs?: NoteRecord[];
  notes?: NoteRecord[];
}

export interface InvoiceExternalAdjustment {
  amount: number;
  comment: string;
}

export interface InvoiceAdjustmentReason {
  reasonId: number;
  adjustmentReason: string;
}

export interface InvoiceInternalAdjustment {
  adjustmentId: number;
  amount: number;
  reasons: InvoiceAdjustmentReason[];
  comment: string;
  attachments?: DocumentsResponse[];
  isCommentVisibleToParticipant: boolean;
}

export interface Invoice extends GridInvoice {
  subtotal: number;
  total: number;
  materialsGroups?: InvoiceMaterialGroup[];
  objectIds: string[];
  attachments: DocumentsResponse[];
  externalAdjustment?: InvoiceExternalAdjustment;
  internalAdjustments?: InvoiceInternalAdjustment[];
  synchronizationStatusCode?: GPSyncStatusCode;
  isPostedToGP?: boolean;
  invoiceFeeSubTotal: number;
  invoiceFees: InvoiceFee[];
  paymentBalanceAdjustments: BalanceAdjustment[];
  balanceAdjustmentsSubTotal: number;
  transactionFeeSubTotal: number;
  transactions: InvoiceTransaction[];
  unidentifiedMaterials: InvoiceMaterial[];
}

export interface RawInvoiceTransaction {
  completedDate: string;
  transactionNumber: string;
  transactionType: InvoiceTransactionType;
  transactionFees: InvoiceFee[];
  materials: InvoiceMaterialRaw[];
}

export interface InvoiceTransaction {
  completedDate: string;
  transactionNumber: string;
  sourceName?: string;
  destinationName?: string;
  transactionType: InvoiceTransactionType;
  transactionFees: InvoiceFee[];
  materials: InvoiceMaterial[];
  weight: number;
}

export interface InvoiceFee {
  transactionId: number;
  specialFeeId: number;
  feeTypeId: number;
  calculatedFee: number;
  fee: number;
  feeName: string;
}

export interface BalanceAdjustment {
  amount: number;
  name: string;
  paymentBalanceAdjustmentId: number;
}

export enum InvoiceAction {
  Approve = "Approve",
  CreateAdjustment = "CreateAdjustment",
}

export interface GridInvoiceList<T extends GridInvoice> {
  total: number;
  invoices: T[];
}

export interface InvoiceMaterialRaw {
  materialCode: string;
  rate: number;
  weight: number;
  materialName: string;
  materialCategoryName: MaterialCategoryName;
  materialId: number;
  price: number;
  sourceName?: string;
  destinationName?: string;
  invoiceFeeSubTotal: number;
  invoiceFees: InvoiceFee[];
  transactionFeeSubTotal: number;
  transactions: RawInvoiceTransaction[];
}

export interface InvoiceMaterialGroupRaw {
  unit?: MaterialUnit;
  transactionType: InvoiceTransactionType;
  categoryName?: MaterialCategoryName;
  materials: InvoiceMaterialRaw[];
}
